import { useState, useRef, useEffect } from 'react';
import '../App.css';
import axios from 'axios';
import FrameList from './FrameList';
import MatList from './MatList';
import Swal from 'sweetalert2';

function FrameYourPaperArt({ image, onEditImage }) {
  /* ================= IMAGE ================= */
  const [userImage, setUserImage] = useState(() => {
    if (image) return image;

    const stored = localStorage.getItem('opticon_last_image');
    return stored || null;
  });
  const fileInputRef = useRef(null);
  /* ================= SIZE ================= */
  const [width, setWidth] = useState(30);
  const [height, setHeight] = useState(30);
  const [imageAspect, setImageAspect] = useState(null);
  const [sizeMode, setSizeMode] = useState('manual');
  const [imagePxSize, setImagePxSize] = useState(null);
  /* ================= IMAGE SIZE DISPLAY ================= */
  const PRINT_DPI = 300;
  const pxToCm = px => ((px / PRINT_DPI) * 2.54).toFixed(1);
  /* ================= FRAME ================= */
  const [frameOpen, setFrameOpen] = useState(false);
  const [selectedFrame, setSelectedFrame] = useState(null);

  /* ================= MAT ================= */
  const [matOpen, setMatOpen] = useState(false);
  const [activeMatIndex, setActiveMatIndex] = useState(0);
  const MAT_PRICE_PER_CM = 1.32;
  const [mats, setMats] = useState([
    {
      mat: null,
      mode: 'equal', // 'equal' | 'custom'
      thickness: 0,  // used for equal
      custom: { top: 0, bottom: 0, left: 0, right: 0 } // used for custom
    },
    {
      mat: null,
      mode: 'equal',
      thickness: 0,
      custom: { top: 0, bottom: 0, left: 0, right: 0 }
    },
    {
      mat: null,
      mode: 'equal',
      thickness: 0,
      custom: { top: 0, bottom: 0, left: 0, right: 0 }
    }
  ]);
  const MAT_MIN_CM = 1;
  const MAT_MAX_CM = 15;
  const MAT_STEP_CM = 1;

  const [vGrooveOpen, setVGrooveOpen] = useState(false);
  const [selectedVGroove, setSelectedVGroove] = useState(null);
  const vGrooveOptions = [
    { id: 'no', label: 'No V-Groove', price: 0 },
    { id: 'small', label: 'Small', price: 15 },
    { id: 'medium', label: 'Medium', price: 20 },
    { id: 'large', label: 'Large', price: 25 }
  ];
  const [vGrooveType, setVGrooveType] = useState('no');
  const vGroovePrice = vGrooveOptions.find(v => v.id === vGrooveType)?.price || 0;

  /* ================= GLASS ================= */
  const [glassType, setGlassType] = useState('none');
  const glassOptions = [
    { id: 'none', label: 'No Glass', price: 0 },
    { id: 'standard_glass', label: 'Standard Glass', price: 15 },
    { id: 'museum_uv_glass', label: 'Museum UV Non Reflective Glass', price: 25 },
    { id: 'clear_perspex', label: 'Clear Perspex', price: 35 }
  ];

  const glassPrice = glassOptions.find(g => g.id === glassType)?.price || 0;

  /* ================= SPACER ================= */
  const spacerOptions = [
    { id: 'no', label: 'No Spacer', price: 0 },
    { id: 'small', label: 'Small', price: 20 },
    { id: 'medium', label: 'Medium', price: 25 },
    { id: 'large', label: 'Large', price: 35 }
  ];
  // State to track selected spacer
  const [spacerType, setSpacerType] = useState('no');
  // Compute selected spacer price
  const spacerPrice = spacerOptions.find(s => s.id === spacerType)?.price || 0;

  const [backingType, setBackingType] = useState('none');
  const backingOptions = [
    { id: 'none', label: 'No Backing' },
    { id: 'adhesive_foamcore', label: 'Adhesive Foamcore' },
    { id: 'non_adhesive_foamcore', label: 'Non-Adhesive Foamcore' }
  ];

  const updateMat = (index, data) => {
    setMats(prev => {
      const copy = [...prev];

      copy[index] = {
        mat: copy[index]?.mat || null,
        mode: copy[index]?.mode || 'equal',
        thickness: copy[index]?.thickness || 0,
        custom: copy[index]?.custom || { top: 0, bottom: 0, left: 0, right: 0 },
        ...data
      };

      return copy;
    });
  };

  const hasMat = index => mats[index]?.mat !== null;


  /* ================= CANVAS ================= */
  const canvasRef = useRef(null);

  /* ================= LOADER ================= */
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [sizeTouched, setSizeTouched] = useState(false);

  /* ================= IMAGE UPLOAD ================= */
  const handleImageUpload = (e) => {
    if (!e.target.files?.length) return;

    const file = e.target.files[0];

    // ✅ Validate image type
    if (!file.type.startsWith('image/')) return;

    const MAX_MB = 64;

    // ✅ Validate size (64MB limit)
    if (file.size > MAX_MB * 1024 * 1024) {
      Swal.fire({
        icon: 'error',
        title: 'Image too large',
        text: 'Please upload an image under 64MB.',
        confirmButtonText: 'OK'
      });
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      const img = new Image();

      img.onload = () => {
        const imgW = img.naturalWidth;
        const imgH = img.naturalHeight;

        setImagePxSize({ w: imgW, h: imgH });

        // AUTO size from image (only initially)
        const aspect = imgW / imgH;
        const defaultW = 30;
        const defaultH = Math.round(defaultW / aspect);

        setWidth(defaultW);
        setHeight(defaultH);
        setSizeTouched(false);

        setUserImage(reader.result);
        localStorage.setItem('opticon_last_image', reader.result);
      };

      img.src = reader.result;
    };

    reader.readAsDataURL(file);
  };

  /* ================= FRAME OPTIONS ================= */
  const frameOptions = FrameList;

  /* ================= MAT OPTIONS ================= */
  const matOptions = MatList;

  const totalMatPrice = mats.reduce((sum, m) => {
    if (!m.mat) return sum;

    const basePrice = m.mat.price || 0;

    let thicknessTotalCm = 0;

    if (m.mode === 'equal') {
      thicknessTotalCm = m.thickness;
    } else {
      thicknessTotalCm =
        m.custom.top +
        m.custom.bottom +
        m.custom.left +
        m.custom.right;
    }

    const thicknessPrice = thicknessTotalCm * MAT_PRICE_PER_CM;

    return sum + basePrice + thicknessPrice;
  }, 0);

  /* ================= GST ================= */
  const GST_RATE = 0.1; // 10%
  const totalPrice = (selectedFrame?.price || 0) + totalMatPrice + vGroovePrice + spacerPrice + glassPrice;
  const gstAmount = totalPrice * GST_RATE;
  const totalWithGST = totalPrice + gstAmount;

  /* ================= CANVAS RENDER ================= */
  const DEFAULT_CANVAS_WIDTH = 520;
  const DEFAULT_CANVAS_HEIGHT = 360;

  const updateCanvasSize = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const PREVIEW_PPCM = 20;
    const PREVIEW_PPPX = 0.15;

    let previewW, previewH;

    if (userImage && imagePxSize?.w && imagePxSize?.h) {
      previewW = imagePxSize.w * PREVIEW_PPPX;
      previewH = imagePxSize.h * PREVIEW_PPPX;
    } else {
      previewW = width * PREVIEW_PPCM;
      previewH = height * PREVIEW_PPCM;
    }

    canvas.width = Math.round(previewW);
    canvas.height = Math.round(previewH);
  };

  const renderCanvas = async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const W = canvas.width;
    const H = canvas.height;

    ctx.clearRect(0, 0, W, H);

    /* ================= UNITS ================= */
    const PREVIEW_PX_PER_CM = 10;

    // Frame thickness comes from backend in mm → convert to cm → preview px
    const frameTpx =
      ((selectedFrame?.thickness || 0) / 10) * PREVIEW_PX_PER_CM;

    /* ================= SAFETY CLAMP ================= */
    const maxInset = Math.min(W, H) / 2 - 4;
    let insetPx = Math.min(frameTpx, maxInset);

    /* ================= MULTI MATS ================= */
    for (const m of mats) {
      if (!m.mat) continue;

      let topPx, bottomPx, leftPx, rightPx;

      if (m.mode === 'equal') {
        const px = m.thickness * PREVIEW_PX_PER_CM;
        topPx = bottomPx = leftPx = rightPx = px;
      } else {
        topPx = m.custom.top * PREVIEW_PX_PER_CM;
        bottomPx = m.custom.bottom * PREVIEW_PX_PER_CM;
        leftPx = m.custom.left * PREVIEW_PX_PER_CM;
        rightPx = m.custom.right * PREVIEW_PX_PER_CM;
      }

      const ox = insetPx + leftPx;
      const oy = insetPx + topPx;

      const outerX = insetPx;
      const outerY = insetPx;
      const outerW = W - insetPx * 2;
      const outerH = H - insetPx * 2;

      if (m.mat.img) {
        const matImg = new Image();
        matImg.src = m.mat.img;
        await new Promise(res => (matImg.onload = res));
        ctx.fillStyle = ctx.createPattern(matImg, 'repeat');
      } else {
        ctx.fillStyle = '#f4f4f4';
      }

      ctx.fillRect(outerX, outerY, outerW, outerH);

      ctx.clearRect(
        insetPx + leftPx,
        insetPx + topPx,
        W - insetPx * 2 - leftPx - rightPx,
        H - insetPx * 2 - topPx - bottomPx
      );

      insetPx += Math.max(topPx, bottomPx, leftPx, rightPx);
    }


    /* ================= IMAGE ================= */
    if (userImage) {
      const img = new Image();
      img.src = userImage;
      await new Promise(res => (img.onload = res));

      ctx.drawImage(
        img,
        insetPx,
        insetPx,
        W - insetPx * 2,
        H - insetPx * 2
      );
    }

    /* ================= FRAME ================= */
    if (selectedFrame?.img && frameTpx > 0) {
      const frameImg = new Image();
      frameImg.src = selectedFrame.img;
      await new Promise(res => (frameImg.onload = res));

      // Top
      ctx.drawImage(frameImg, 0, 0, W, frameTpx);
      // Bottom
      ctx.drawImage(frameImg, 0, H - frameTpx, W, frameTpx);
      // Left
      ctx.drawImage(frameImg, 0, 0, frameTpx, H);
      // Right
      ctx.drawImage(frameImg, W - frameTpx, 0, frameTpx, H);
    }
  };


  /* ================= ADD TO CART ================= */
  const handleAddToCart = async () => {
    if (!canvasRef.current) return;

    setLoading(true);
    setProgress(0);

    try {

      const imageToSend =
        image || userImage || canvasRef.current.toDataURL('image/png');

      /* ================= MULTI MATS ================= */
      const matMeta = [];

      mats.forEach((m, index) => {
        if (!m.mat) return;

        const i = index + 1;

        // Always send mat name
        matMeta.push({
          key: `mat_${i}_name`,
          value: m.mat.name
        });

        // Send mode
        matMeta.push({
          key: `mat_${i}_mode`,
          value: m.mode
        });

        if (m.mode === 'equal') {

          // Equal thickness (single value)
          matMeta.push({
            key: `mat_${i}_thickness_mm`,
            value: m.thickness * 10 // cm → mm
          });

        } else {

          // Custom 4-side thickness
          ['top', 'bottom', 'left', 'right'].forEach(side => {
            matMeta.push({
              key: `mat_${i}_${side}_mm`,
              value: m.custom[side] * 10 // cm → mm
            });
          });

        }
      });

      /* ================= PAYLOAD ================= */

      const payload = {
        id: 8520,
        quantity: 1,
        meta_data: [
          { key: 'frame_name', value: selectedFrame?.name || '' },
          { key: 'v_groove', value: vGrooveType },
          { key: 'spacer', value: spacerType },
          { key: 'backing_type', value: backingType },
          { key: 'glass_type', value: glassType },
          { key: 'total_price', value: totalWithGST },
          ...matMeta,
          { key: 'edited_image', value: imageToSend }
        ]
      };
      console.log('Payload to send:', payload);
      await axios.post(
        'https://opticonframing.com.au/wp-json/opticon/v1/frame-your-paper-art/add-to-cart',
        payload,
        {
          headers: { 'Content-Type': 'application/json' },
          withCredentials: true,
          onUploadProgress: e => {
            if (!e.total) return;
            setProgress(Math.round((e.loaded * 100) / e.total));
          }
        }
      );

      setProgress(100);

      setTimeout(() => {
        window.location.href = 'https://opticonframing.com.au/cart';
      }, 700);

    } catch (err) {
      console.error(err);
      alert('Add to cart failed');
      setLoading(false);
      setProgress(0);
    }
  };

  const handleRemoveImage = () => {
    setUserImage(null);
    setImageAspect(null);
    setImagePxSize(null); // 🔑 reset image sizing

    setSizeMode('manual');
    setWidth(30);
    setHeight(30);
    setSizeTouched(false);

    localStorage.removeItem('opticon_last_image');
  };

  useEffect(() => {
    (async () => {
      updateCanvasSize();
      renderCanvas();
    })();
  }, [userImage, selectedFrame, mats, width, height]);


  useEffect(() => {
    if (!selectedFrame && frameOptions.length > 0) {
      setSelectedFrame(frameOptions[0]);
    }
  }, [selectedFrame, frameOptions]);

  useEffect(() => {
    if (!userImage || imagePxSize) return;

    const img = new Image();
    img.onload = () => {
      setImageAspect(img.naturalWidth / img.naturalHeight);

      setImagePxSize({
        w: img.naturalWidth,
        h: img.naturalHeight
      });

      setSizeMode('image'); // 🔑 force image-driven sizing
    };

    img.src = userImage;
  }, [userImage, imagePxSize]);

  useEffect(() => {
    if (image) setUserImage(image);
  }, [image]);
  /* ================= UI ================= */
  return (
    <div className="product-page">

      {loading && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
          backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', flexDirection: 'column',
          justifyContent: 'center', alignItems: 'center', zIndex: 9999
        }}>
          {/* Circular Loader */}
          <div style={{
            width: '60px', height: '60px', borderRadius: '50%',
            border: '5px solid rgba(255,255,255,0.1)',
            borderTop: '5px solid #1E5FFF', // Requested Color
            animation: 'spin 1s linear infinite',
            marginBottom: '20px'
          }}></div>

          {/* Percentage Text */}
          <p style={{ color: 'white', fontSize: '24px', fontWeight: 'bold' }}>
            {progress}%
          </p>

          <style>{`
            @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
          `}</style>
        </div>
      )}

      {/* LEFT */}
      <div className="product-image-panel">
        <canvas
          ref={canvasRef}
          className="real-canvas-preview"
        />
      </div>

      {/* RIGHT */}
      <div className="product-info-panel">
        <h1 className="product-title">Frame Your Paper Art</h1>

        {/* IMAGE SIZE */}
        <div className="print-option-row">
          <label className="form-label">Image Size (cm)</label>

          <div className="image-size-row">
            {!userImage && (
              <>
                <input
                  type="number"
                  value={width}
                  onChange={e => {
                    setWidth(Number(e.target.value));
                    setSizeTouched(true);
                    setSizeMode('manual'); // 🔑
                  }}

                  className="form-select"
                />

                <input
                  type="number"
                  value={height}
                  onChange={e => {
                    setHeight(Number(e.target.value));
                    setSizeTouched(true);
                    setSizeMode('manual'); // 🔑
                  }}
                  className="form-select"
                />
              </>
            )}

            {userImage && imagePxSize && (
              <div className="image-dimension-display">
                <span>
                  <strong>
                    {' '}
                    {pxToCm(imagePxSize.w)}×{pxToCm(imagePxSize.h)} cm
                  </strong>
                </span>
              </div>
            )}

            <button
              className="btn-secondary image-upload-btn"
              onClick={() => fileInputRef.current.click()}
            >
              {userImage ? 'Change Image' : 'Add Image'}
            </button>

            {userImage && (
              <button
                className="btn-danger image-remove-btn"
                onClick={handleRemoveImage}
                type="button"
              >
                Remove Image
              </button>
            )}

            <input
              type="file"
              ref={fileInputRef}
              accept="image/*"
              hidden
              onChange={handleImageUpload}
            />
          </div>
        </div>


        {/* FRAME */}
        <div
          className="print-option-row frame-row"
        >
          <label className="form-label">Frame</label>
          <div className="frame-dropdown-display" onClick={() => setFrameOpen(true)}>
            <span>{selectedFrame?.name || 'Select Frame'}</span>
            <span className="frame-arrow">›</span>
          </div>
        </div>

        {/* MATS */}
        {[0, 1, 2].map(i => (
          (i === 0 || hasMat(i - 1)) && (
            <div key={i}>
              <div className="print-option-row mat-row">
                <label className="form-label">Mat {i + 1}</label>
                <div
                  className="frame-dropdown-display"
                  onClick={() => {
                    setActiveMatIndex(i);
                    setMatOpen(true);
                  }}
                >
                  <span>{mats[i].mat?.name || 'Select Mat'}</span>
                  <span className="frame-arrow">›</span>
                </div>
              </div>

              {mats[i].mat && (
                <>
                  {/* Mode Dropdown */}
                  <div className="print-option-row">
                    <label className="form-label">Mat {i + 1} Size Mode</label>
                    <select
                      className="form-select"
                      value={mats[i].mode}
                      onChange={e =>
                        updateMat(i, { mode: e.target.value })
                      }
                    >
                      <option value="equal">Equal Size</option>
                      <option value="custom">Custom Size</option>
                    </select>
                  </div>

                  {/* Equal Mode */}
                  {mats[i].mode === 'equal' && (
                    <div className="print-option-row">
                      <label className="form-label">
                        Width: <strong>{mats[i].thickness} cm</strong>
                      </label>
                      <input
                        type="range"
                        min={MAT_MIN_CM}
                        max={MAT_MAX_CM}
                        step={MAT_STEP_CM}
                        value={mats[i].thickness}
                        onChange={e =>
                          updateMat(i, { thickness: Number(e.target.value) })
                        }
                      />
                    </div>
                  )}

                  {/* Custom Mode */}
                  {mats[i].mode === 'custom' && (
                    <div className="custom-mat-grid">
                      {['top', 'bottom', 'left', 'right'].map(side => (
                        <div key={side} className="print-option-row">
                          <label className="form-label">
                            {side.toUpperCase()}:
                            <strong> {mats[i].custom[side]} cm</strong>
                          </label>
                          <input
                            type="range"
                            min={MAT_MIN_CM}
                            max={MAT_MAX_CM}
                            step={MAT_STEP_CM}
                            value={mats[i].custom[side]}
                            onChange={e =>
                              updateMat(i, {
                                custom: {
                                  ...mats[i].custom,
                                  [side]: Number(e.target.value)
                                }
                              })
                            }
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

            </div>
          )
        ))}


        {/* MAT SIDEBAR */}
        {matOpen && (
          <div className="frame-sidebar open">
            <div className="frame-sidebar-header">
              <h3>Mats</h3>
              <button onClick={() => setMatOpen(false)}>✕</button>
            </div>

            {matOptions.map(mat => (
              <div
                key={mat.id}
                className={`frame-item ${mats[activeMatIndex]?.mat?.id === mat.id ? 'active' : ''
                  }`}
                onClick={() => {
                  updateMat(activeMatIndex, {
                    mat,
                    thickness: mats[activeMatIndex].thickness || 2
                  });

                  setMatOpen(false);
                }}
              >
                <img src={mat.img} alt={mat.name} />
                <div className="frame-meta">
                  <div className="frame-name">{mat.name}</div>
                  <div className="frame-price">
                    {mat.price > 0 ? `+ $${mat.price}` : 'Included'}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* V-GROOVE (only if Mat 1 exists) */}
        {hasMat(0) && (
          <div className="print-option-row">
            <label className="form-label">V-Groove</label>

            <select
              className="form-select"
              value={vGrooveType}
              onChange={e => setVGrooveType(e.target.value)}
            >
              {vGrooveOptions.map(opt => (
                <option key={opt.id} value={opt.id}>
                  {opt.label} {opt.price > 0 ? `(+$${opt.price})` : ''}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* GLASS */}
        <div className="print-option-row">
          <label className="form-label">Glass</label>

          <select
            className="form-select"
            value={glassType}
            onChange={e => setGlassType(e.target.value)}
          >
            {glassOptions.map(opt => (
              <option key={opt.id} value={opt.id}>
                {opt.label} {opt.price > 0 ? `(+$${opt.price})` : ''}
              </option>
            ))}
          </select>
        </div>


        {/* SPACER */}
        <div className="print-option-row">
          <label className="form-label">Spacer</label>

          <select
            className="form-select"
            value={spacerType}
            onChange={e => setSpacerType(e.target.value)}
          >
            {spacerOptions.map(opt => (
              <option key={opt.id} value={opt.id}>
                {opt.label} {opt.price > 0 ? `(+$${opt.price})` : ''}
              </option>
            ))}
          </select>
        </div>

        {/* BACKING */}
        <div className="print-option-row">
          <label className="form-label">Backing</label>

          <select
            className="form-select"
            value={backingType}
            onChange={e => setBackingType(e.target.value)}
          >
            {backingOptions.map(opt => (
              <option key={opt.id} value={opt.id}>
                {opt.label} {opt.price > 0 ? `(+$${opt.price})` : ''}
              </option>
            ))}
          </select>
        </div>

        {/* Price */}
        <div className="price-cart-row">
          <div className="product-price">
            ${totalPrice.toFixed(2)} + GST (10%) ${gstAmount.toFixed(2)} = ${totalWithGST.toFixed(2)}
          </div>
          <button
            className="add-to-cart-btn"
            onClick={handleAddToCart}
            disabled={loading}
          >
            {loading ? `${progress}%` : 'Add to Cart'}
          </button>
        </div>
      </div>

      {/* FRAME SIDEBAR */}
      {frameOpen && (
        <div className="frame-sidebar open">
          <div className="frame-sidebar-header">
            <h3>Frames</h3>
            <button onClick={() => setFrameOpen(false)}>✕</button>
          </div>

          {frameOptions.map(frame => (
            <div
              key={frame.id}
              className={`frame-item ${selectedFrame?.id === frame.id ? 'active' : ''}`}
              onClick={() => {
                setSelectedFrame(frame);
                setFrameOpen(false);
              }}
            >
              <img src={frame.img} alt={frame.name} />
              <div className="frame-meta">
                <div className="frame-name">{frame.name}</div>
                <div className="frame-price">+ ${frame.price}</div>
              </div>
            </div>
          ))}
        </div>
      )}

    </div>
  );
}

export default FrameYourPaperArt;